import React, { useState } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  Link,
  CircularProgress
} from '@mui/material';
import api from '../../utils/api';

function ResetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  // Clear error when user starts typing
  const handleChange = (setter) => (e) => {
    setter(e.target.value);
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading) return;
    
    setLoading(true);
    setError('');

    try {
      if (password !== confirmPassword) {
        throw new Error('Passwords do not match');
      }

      if (password.length < 6) {
        throw new Error('Password must be at least 6 characters long');
      }

      // The correct API path - the api utility already includes the base URL with '/api'
      // So we need to use '/api/auth/reset-password' here
      await api.post('/api/auth/reset-password', { token, password });
      navigate('/', { 
        state: { message: 'Password reset successful. Please sign in with your new password.' } 
      });
    } catch (error) {
      setError(
        error.response?.data?.message || 
        error.message || 
        'Failed to reset password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'secondary.main', // Lavender background
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 4
    }}>
      {/* Logo */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          mb: 4
        }}
      >
        <img src={`${process.env.PUBLIC_URL}/img/logo.svg`} alt="Idotoo Logo" style={{ height: '40px' }} />
      </Box>

      {/* Reset Password Card */}
      <Paper 
        elevation={0}
        sx={{ 
          backgroundColor: 'background.paper',
          maxWidth: '400px',
          width: '100%',
          mx: 2,
          overflow: 'hidden',
          border: '1px solid rgba(0, 0, 0, 0.08)'
        }}
      >
        <Box sx={{ p: 4 }}>
          <Typography 
            variant="h5" 
            component="h2" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 500,
              color: 'black',
              mb: 2
            }}
          >
            Reset Password
          </Typography>
          <Typography 
            variant="body2" 
            align="center" 
            sx={{ 
              mb: 3,
              color: 'text.secondary'
            }}
          >
            Enter your new password below
          </Typography>

          {!token && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Invalid or missing reset token. Please request a new password reset link.
            </Alert>
          )}
          
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              placeholder="New Password"
              type="password"
              value={password}
              onChange={handleChange(setPassword)}
              margin="normal"
              required
              variant="outlined"
              disabled={!token}
              autoFocus
              sx={{ 
                mb: 2,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff'
                  }
              }}
            />
            <TextField
              fullWidth
              placeholder="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={handleChange(setConfirmPassword)}
              margin="normal"
              required
              variant="outlined"
              disabled={!token}
              sx={{ 
                mb: 3,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff'
                  }
              }}
            />
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading || !token}
              sx={{ 
                py: 1.5,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                border: '1px solid #000',
                color: '#fff',
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
            </Button>
          </Box>
          
          <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Link 
              component={RouterLink} 
              to="/" 
              sx={{ 
                color: 'text.secondary',
                fontSize: '0.875rem',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Back to sign in
            </Link>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}

export default ResetPassword; 